import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Zap, Cpu, DollarSign } from 'lucide-react';
type Model = {
  name: string;
  type: string;
  latency: string;
  throughput: string;
  power: string;
  price: string;
  provider: string;
  category: 'Language' | 'Vision' | 'Audio';
};
const models: Model[] = [{
  name: 'ResNet-50',
  type: 'CNN',
  latency: '0.3ms',
  throughput: '15,000',
  power: '12W',
  price: '$0.02',
  provider: 'Xilinx Alveo U250',
  category: 'Vision'
}, {
  name: 'BERT-Base',
  type: 'Transformer',
  latency: '0.8ms',
  throughput: '5,200',
  power: '18W',
  price: '$0.05',
  provider: 'Intel Agilex',
  category: 'Language'
}, {
  name: 'YOLOv8',
  type: 'Detection',
  latency: '0.5ms',
  throughput: '8,000',
  power: '15W',
  price: '$0.03',
  provider: 'Xilinx Versal',
  category: 'Vision'
}, {
  name: 'GPT-2 Small',
  type: 'Transformer',
  latency: '1.2ms',
  throughput: '3,800',
  power: '22W',
  price: '$0.08',
  provider: 'Achronix Speedster',
  category: 'Language'
}, {
  name: 'Whisper Tiny',
  type: 'Audio',
  latency: '0.6ms',
  throughput: '6,500',
  power: '14W',
  price: '$0.04',
  provider: 'Lattice CertusPro',
  category: 'Audio'
}, {
  name: 'Stable Diffusion',
  type: 'Diffusion',
  latency: '4.5ms',
  throughput: '220',
  power: '35W',
  price: '$0.15',
  provider: 'Intel Stratix 10',
  category: 'Vision'
}, {
  name: 'LLaMA-7B',
  type: 'LLM',
  latency: '2.1ms',
  throughput: '1,800',
  power: '28W',
  price: '$0.12',
  provider: 'Xilinx Alveo U280',
  category: 'Language'
}, {
  name: 'EfficientNet-B4',
  type: 'CNN',
  latency: '0.4ms',
  throughput: '12,000',
  power: '10W',
  price: '$0.02',
  provider: 'Microchip PolarFire',
  category: 'Vision'
}];
const tabs = ['All Models', 'Language', 'Vision', 'Audio'];
export function ModelShowcase() {
  const [activeTab, setActiveTab] = useState('All Models');
  const [searchQuery, setSearchQuery] = useState('');
  const filteredModels = models.filter(model => {
    const matchesTab = activeTab === 'All Models' || model.category === activeTab;
    const matchesSearch = model.name.toLowerCase().includes(searchQuery.toLowerCase()) || model.provider.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });
  return <div className="py-24 bg-gate-dark" data-id="element-131">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-132">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6" data-id="element-133">
          <div data-id="element-134">
            <h2 className="text-3xl font-bold text-white mb-2" data-id="element-135">
              Optimized Models
            </h2>
            <p className="text-gate-text-secondary" data-id="element-136">
              Browse FPGA-ready bitstreams available instantly.
            </p>
          </div>

          <div className="relative" data-id="element-137">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gate-text-muted" data-id="element-138" />
            <input type="text" placeholder="Search models..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 pr-4 py-2 bg-gate-surface border border-gate-border rounded-lg text-sm text-white focus:outline-none focus:border-gate-teal w-full md:w-64" data-id="element-139" />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-gate-surface/50 p-1 rounded-lg w-fit mb-8 border border-gate-border/50" data-id="element-140">
          {tabs.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`relative px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === tab ? 'text-white' : 'text-gate-text-muted hover:text-white'}`} data-id="element-141">
              {activeTab === tab && <motion.div layoutId="activeTab" className="absolute inset-0 bg-gate-teal/10 border border-gate-teal/20 rounded-md" transition={{
            type: 'spring',
            bounce: 0.2,
            duration: 0.6
          }} data-id="element-142" />}
              <span className="relative z-10" data-id="element-143">{tab}</span>
            </button>)}
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border border-gate-border bg-gate-surface/30 backdrop-blur-sm" data-id="element-144">
          <table className="w-full text-left border-collapse" data-id="element-145">
            <thead data-id="element-146">
              <tr className="border-b border-gate-border text-xs font-medium text-gate-text-muted uppercase tracking-wider" data-id="element-147">
                <th className="px-6 py-4" data-id="element-148">Model Name</th>
                <th className="px-6 py-4" data-id="element-149">Architecture</th>
                <th className="px-6 py-4 text-right" data-id="element-150">Latency</th>
                <th className="px-6 py-4 text-right" data-id="element-151">Throughput (tok/s)</th>
                <th className="px-6 py-4 text-right" data-id="element-152">Power</th>
                <th className="px-6 py-4 text-right" data-id="element-153">Price / 1M</th>
                <th className="px-6 py-4" data-id="element-154">Provider</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gate-border/50" data-id="element-155">
              <AnimatePresence mode="wait" data-id="element-156">
                {filteredModels.map((model, index) => <motion.tr key={model.name} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                delay: index * 0.05
              }} className="group hover:bg-gate-surface/80 transition-colors" data-id="element-157">
                    <td className="px-6 py-4" data-id="element-158">
                      <div className="font-medium text-white group-hover:text-gate-teal transition-colors" data-id="element-159">
                        {model.name}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-160">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gate-border/50 text-gate-text-secondary" data-id="element-161">
                        {model.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-mono text-gate-teal" data-id="element-162">
                      {model.latency}
                    </td>
                    <td className="px-6 py-4 text-right font-mono text-white" data-id="element-163">
                      {model.throughput}
                    </td>
                    <td className="px-6 py-4 text-right font-mono text-gate-amber" data-id="element-164">
                      {model.power}
                    </td>
                    <td className="px-6 py-4 text-right font-mono text-white" data-id="element-165">
                      {model.price}
                    </td>
                    <td className="px-6 py-4 text-sm text-gate-text-secondary" data-id="element-166">
                      <div className="flex items-center gap-2" data-id="element-167">
                        <Cpu className="w-3 h-3" data-id="element-168" />
                        {model.provider}
                      </div>
                    </td>
                  </motion.tr>)}
              </AnimatePresence>
            </tbody>
          </table>

          {filteredModels.length === 0 && <div className="py-12 text-center text-gate-text-muted" data-id="element-169">
              No models found matching your criteria.
            </div>}
        </div>
      </div>
    </div>;
}